import { Group, Input, Select, Stack, TextInput } from "@mantine/core";
import * as countryCodes from "country-codes-list";
import {
  getCountryCallingCode,
  parsePhoneNumberFromString,
  type CountryCode,
} from "libphonenumber-js";
import { useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

const DEFAULT_COUNTRY: CountryCode = "FR";

interface Props {
  value?: string | null;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: ReactNode;
  disabled?: boolean;
  defaultCountry?: CountryCode;
  label?: string;
  onBlur?: () => void;
}

const COUNTRY_OPTIONS = countryCodes
  .all()
  .filter(
    (country, index, countries) =>
      countries.findIndex(
        ({ countryCode }) => countryCode === country.countryCode
      ) === index
  )
  .map(({ countryCode, countryNameLocal, flag, countryCallingCode }) => ({
    value: countryCode,
    label: `${flag} ${countryNameLocal} (+${countryCallingCode})`,
  }))
  .sort((left, right) => left.label.localeCompare(right.label));

const getPhoneNumberState = (
  value: string | null | undefined,
  fallbackCountry: CountryCode
) => {
  if (!value) {
    return {
      country: fallbackCountry,
      nationalValue: "",
    };
  }

  const parsedPhoneNumber = parsePhoneNumberFromString(value);

  if (!parsedPhoneNumber) {
    return {
      country: fallbackCountry,
      nationalValue: value.replace(/^\+/, ""),
    };
  }

  return {
    country: (parsedPhoneNumber.country ?? fallbackCountry) as CountryCode,
    nationalValue: parsedPhoneNumber.nationalNumber,
  };
};

const buildPhoneNumberValue = (nationalValue: string, country: CountryCode) => {
  const trimmedValue = nationalValue.trim();

  if (!trimmedValue) {
    return "";
  }

  const parsedPhoneNumber = parsePhoneNumberFromString(trimmedValue, country);

  if (parsedPhoneNumber) {
    return parsedPhoneNumber.number;
  }

  const digitsOnly = trimmedValue.replace(/\D/g, "");

  if (!digitsOnly) {
    return "";
  }

  return `+${getCountryCallingCode(country)}${digitsOnly}`;
};

export const PhoneNumberInput = ({
  value,
  onChange,
  placeholder,
  error,
  disabled = false,
  defaultCountry = DEFAULT_COUNTRY,
  label,
  onBlur,
}: Props) => {
  const { t } = useTranslation("forms");
  const parsedValue = useMemo(
    () => getPhoneNumberState(value, defaultCountry),
    [defaultCountry, value]
  );
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>();
  const [draftNationalValue, setDraftNationalValue] = useState<string>();
  const [isEditingNationalValue, setIsEditingNationalValue] = useState(false);

  const country = selectedCountry ?? parsedValue.country;
  const nationalValue = isEditingNationalValue
    ? (draftNationalValue ?? parsedValue.nationalValue)
    : parsedValue.nationalValue;

  const emitChange = (nextCountry: CountryCode, nextNationalValue: string) => {
    onChange(buildPhoneNumberValue(nextNationalValue, nextCountry));
  };

  return (
    <Stack gap="xs">
      {label && <Input.Label>{label}</Input.Label>}
      <Group w="100%" gap="xs" wrap="nowrap" align="flex-start">
        <Select
          w={260}
          searchable
          allowDeselect={false}
          onBlur={onBlur}
          aria-label={t("components.phoneNumberInput.countryAriaLabel")}
          value={country}
          disabled={disabled}
          data={COUNTRY_OPTIONS}
          onFocus={e => {
            e.currentTarget.select();
          }}
          onClick={e => {
            e.currentTarget.select();
          }}
          onChange={nextCountry => {
            if (!nextCountry) {
              return;
            }

            const resolvedCountry = nextCountry as CountryCode;

            setSelectedCountry(resolvedCountry);
            emitChange(resolvedCountry, nationalValue);
          }}
        />
        <TextInput
          flex={1}
          type="tel"
          aria-label={t("components.phoneNumberInput.phoneNumberAriaLabel")}
          autoComplete="tel-national"
          placeholder={
            placeholder ?? t("components.phoneNumberInput.placeholder")
          }
          value={nationalValue}
          error={error}
          disabled={disabled}
          onFocus={() => {
            setIsEditingNationalValue(true);
            setDraftNationalValue(parsedValue.nationalValue);
          }}
          onBlur={() => {
            setIsEditingNationalValue(false);
            setDraftNationalValue(undefined);
            onBlur?.();
          }}
          onChange={event => {
            const nextNationalValue = event.currentTarget.value;

            setDraftNationalValue(nextNationalValue);
            emitChange(country, nextNationalValue);
          }}
        />
      </Group>
    </Stack>
  );
};
